import type { ValidationError } from "@/types/errors";
import { parseErrorMessage } from "@/utils/errors";

interface PlaygroundEvaluateErrorResponse {
  success: false;
  message: string | ValidationError[];
}

const isValidationErrorList = (value: unknown): value is ValidationError[] =>
  Array.isArray(value) &&
  value.every(error => {
    if (typeof error !== "object" || error === null) {
      return false;
    }

    const record = error as Record<string, unknown>;
    return (
      Array.isArray(record.loc) &&
      record.loc.every(part => typeof part === "string" || typeof part === "number") &&
      typeof record.msg === "string" &&
      typeof record.type === "string"
    );
  });

const isPlaygroundEvaluateErrorResponse = (
  value: unknown,
): value is PlaygroundEvaluateErrorResponse => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const response = value as Record<string, unknown>;
  return (
    response.success === false &&
    (typeof response.message === "string" || isValidationErrorList(response.message))
  );
};

export const handlePlaygroundEvaluateResponse = async <T>(response: Response): Promise<T> => {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error(
      response.ok
        ? "The Evaluate API returned an invalid response."
        : `Evaluate request failed (${response.status}).`,
    );
  }

  if (isPlaygroundEvaluateErrorResponse(body)) {
    const message =
      typeof body.message === "string" ? body.message : parseErrorMessage(body.message);
    throw new Error(message || `Evaluate request failed (${response.status}).`);
  }

  if (!response.ok) {
    throw new Error(`Evaluate request failed (${response.status}).`);
  }

  if (typeof body !== "object" || body === null || !("success" in body) || body.success !== true) {
    throw new Error("The Evaluate API returned an invalid response.");
  }

  return body as T;
};
